import React, { useEffect, useState, useMemo } from "react";
import api from "../../services/api";
import { II } from "../../components/common/Icons";
import "../../assets/styles/HR/HrReportsPg.css";

// ── helpers ──
const _mKey = (d) => {
  if (!d) return null;
  try { const dt=new Date(d); if(isNaN(dt)) return null; return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`; } catch { return null; }
};
const _mLabel = (k) => {
  if (!k) return "";
  const [y,m]=k.split("-");
  return new Date(Number(y),Number(m)-1,1).toLocaleDateString("en-IN",{month:"long",year:"numeric"});
};
const _fmt = (d) => { try { return d?new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}):"—"; } catch{return "—";} };

function exportReportsCSV(rows, filename) {
  const headers = ["Report ID","Title","Type","PM/Manager","Status","PM Comment","File","Sent On"];
  const lines = [headers.join(",")];
  rows.forEach(r => {
    lines.push([
      r.report_id||"", r.title||"", r.type||r.report_type||"",
      r.emp_name||r.pm_name||"", r.status||"",
      r.pm_note||"", r.file_name||"",
      r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN") : ""
    ].map(v=>`"${String(v||"").replace(/"/g,'""')}"`).join(","));
  });
  const blob = new Blob([lines.join("\n")],{type:"text/csv;charset=utf-8;"});
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href=url; a.download=filename; a.click();
  URL.revokeObjectURL(url);
}

function HrReportsPg() {
  const [reports, setReports] = useState([]);
  const [pms, setPms] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [active, setActive] = useState(null);
  const [logs, setLogs] = useState([]);
  
  const [form, setForm] = useState({
    title: "",
    pmId: "",
    projectId: "",
    reportType: "Compliance",
    comment: "",
    file: null
  });
  const [resubmitComment, setResubmitComment] = useState("");
  const [previewData, setPreviewData] = useState({ open: false, url: "", name: "", mimeType: "" });
  const [resubmitFile, setResubmitFile] = useState(null);
  const [toast, setToast] = useState({ type: "", message: "" });
  const [viewer, setViewer] = useState({ open: false, url: "", name: "" });
  const [selectedPMs, setSelectedPMs] = useState([]);   // [{id, name, emp_id}]
  const [showPMPicker, setShowPMPicker] = useState(false);
  const [pmSearch, setPmSearch] = useState("");
  const [lostReports, setLostReports] = useState([]);
  const [lostFilter, setLostFilter] = useState("all");
  const [sendingNotice, setSendingNotice] = useState(null);
  const [hrNoticeComment, setHrNoticeComment] = useState("");
  const [lostDetailModal, setLostDetailModal] = useState(null);
  const [rptMonthFilter, setRptMonthFilter] = useState("all");
  const [showRptMDrop,   setShowRptMDrop]   = useState(false);

  const handlePreview = async (fileUrl, fileName) => {
    try {
      const fetchUrl = api.buildProxyUrl(fileUrl, fileName, 'inline');
      const token = api.getToken();
      
      const response = await fetch(fetchUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) throw new Error('Preview failed');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      setPreviewData({
        open: true,
        url: blobUrl,
        name: fileName || "Report",
        mimeType: blob.type
      });
    } catch (error) {
      alert("Preview Error: " + error.message);
    }
  };

  const loadLostReports = async () => {
    try {
      const d = await api.getHrLostReports();
      setLostReports(Array.isArray(d) ? d : []);
    } catch {}
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r, managers, prj] = await Promise.all([api.getHrReports(), api.getPMs(), api.getHrReportProjects().catch(() => [])]);
      const reportRows = Array.isArray(r) ? r : [];
      setReports(reportRows);
      setPms(Array.isArray(managers) ? managers : []);
      setProjects(Array.isArray(prj) ? prj : []);
      return reportRows;
    } catch {
      setReports([]);
      setPms([]);
      setProjects([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadLostReports(); }, []);

  const mine = reports;

  const isLostAsset = (r) => String(r.type || "").toLowerCase().includes("lost");

  const downloadReportText = (r) => {
    const content = r.pm_note
      ? (r.hr_comment || "") + "\n\n--- PM COMMENT ---\n" + r.pm_note
      : (r.hr_comment || r.title || "No content");
    const blob = new Blob([content], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${r.report_id || "report"}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const openLogs = async (report) => {
    setActive(report);
    try {
      const rows = await api.getHrReportLogs(report.report_id);
      setLogs(Array.isArray(rows) ? rows : []);
    } catch {
      setLogs([]);
    }
  };

  const sendReport = async () => {
    if (!selectedPMs.length || !form.file) return;
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append("title", form.title || "HR Report");
      fd.append("pmIds", JSON.stringify(selectedPMs.map(p => p.id)));
      fd.append("projectId", form.projectId || "");
      fd.append("reportType", form.reportType);
      fd.append("comment", form.comment || "");
      fd.append("file", form.file);
      const result = await api.uploadHrReport(fd);
      setForm({ title: "", pmId: "", projectId: "", reportType: "Compliance", comment: "", file: null });
      setSelectedPMs([]);
      await load();
      setToast({ type: "ok", message: `✅ Report sent to ${selectedPMs.length} manager${selectedPMs.length > 1 ? "s" : ""}!` });
    } catch (e) {
      setToast({ type: "err", message: `Send failed: ${e.message}` });
    } finally {
      setSubmitting(false);
      setTimeout(() => setToast({ type: "", message: "" }), 3000);
    }
  };

  const togglePM = (pm) => {
    setSelectedPMs(prev =>
      prev.find(p => p.id === pm.id)
        ? prev.filter(p => p.id !== pm.id)
        : [...prev, pm]
    );
  };

  const filteredPMs = pms.filter(p =>
    !pmSearch || p.name.toLowerCase().includes(pmSearch.toLowerCase()) ||
    (p.emp_id || "").toLowerCase().includes(pmSearch.toLowerCase())
  );

  const resubmit = async () => {
    if (!active?.report_id || !resubmitFile) return;
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append("comment", resubmitComment || "");
      fd.append("file", resubmitFile);
      await api.resubmitHrReport(active.report_id, fd);
      setResubmitComment("");
      setResubmitFile(null);
      await openLogs(active);
      await load();
      setToast({ type: "ok", message: "Report resubmitted successfully." });
    } catch (e) {
      setToast({ type: "err", message: `Resubmit failed: ${e.message}` });
    } finally {
      setSubmitting(false);
      setTimeout(() => setToast({ type: "", message: "" }), 2600);
    }
  };

  const exportLostCSV = (rows) => {
    const headers = ["Report ID","Asset ID","Asset Name","Asset Tag","Asset Value (₹)","Fine Amount (₹)","Lost Date","Issued Date","Employee","PM","PM Comment","HR Comment","Status","Sent At"];
    const lines = [headers.join(",")];
    rows.forEach(r => {
      lines.push([
        r.report_id||"", r.asset_id||"", `"${(r.asset_name||"").replace(/"/g,'""')}"`,
        r.asset_tag||"",
        r.asset_value||0, r.fine_amount||0,
        r.lost_date ? new Date(r.lost_date).toLocaleDateString("en-IN") : "",
        r.issued_date ? new Date(r.issued_date).toLocaleDateString("en-IN") : "",
        `"${(r.employee_name||r.employee_full_name||"").replace(/"/g,'""')}"`,
        `"${(r.pm_name||r.pm_full_name||"").replace(/"/g,'""')}"`,
        `"${(r.pm_comment||"").replace(/"/g,'""')}"`,
        `"${(r.hr_comment||"").replace(/"/g,'""')}"`,
        r.status||"", r.hr_sent_at ? new Date(r.hr_sent_at).toLocaleDateString("en-IN") : ""
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type:"text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = "lost_asset_reports.csv"; a.click(); URL.revokeObjectURL(a.href);
  };

  const handleSendToEmployee = async (r) => {
    if (!hrNoticeComment.trim()) { setToast({ type:"err", message:"Please add a comment for the employee" }); setTimeout(()=>setToast({type:"",message:""}),2600); return; }
    setSendingNotice(r.report_id);
    try {
      await api.sendLostNoticeToEmployee({ reportId: r.report_id, hrComment: hrNoticeComment });
      setHrNoticeComment(""); setLostDetailModal(null);
      await loadLostReports();
      setToast({ type:"ok", message:"✅ Employee notified successfully!" });
    } catch (e) { setToast({ type:"err", message:"Failed: " + e.message }); }
    setSendingNotice(null);
    setTimeout(() => setToast({type:"",message:""}), 3000);
  };

  const fmtDate = (d) => { try { return d ? new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}) : "—"; } catch { return "—"; }};

  const lostFiltered = lostFilter === "all" ? lostReports
    : lostFilter === "pending" ? lostReports.filter(r => r.status !== "Notified Employee")
    : lostReports.filter(r => r.status === "Notified Employee");

  // ── Reports derived ──
  const rptAvailMonths = useMemo(()=>{
    const set=new Set(); mine.forEach(r=>{const k=_mKey(r.created_at);if(k)set.add(k);});
    return ["all",...Array.from(set).sort((a,b)=>b.localeCompare(a))];
  },[mine]);

  const displayReports = useMemo(()=>{
    if(rptMonthFilter==="all") return mine;
    return mine.filter(r=>_mKey(r.created_at)===rptMonthFilter);
  },[mine, rptMonthFilter]);

  return (
    <div>
      <div className="sec">Reports</div>
      {toast.message &&
      <div className="toast hrreportspg-dyn-1" style={{ "--hrreportspg-1-background":
        toast.type === "ok" ? "#eff6ff" : "#fef2f2", "--hrreportspg-1-border":
        `1px solid ${toast.type === "ok" ? "#bfdbfe" : "#fecaca"}`, "--hrreportspg-1-color":
        toast.type === "ok" ? "#166534" : "#991b1b" }}>
        
          {toast.message}
        </div>
      }
      {/* ═══ LOST ASSET REPORTS SECTION ═══ */}
      {lostReports.length > 0 && (
        <div className="cd" style={{marginBottom:18}}>
          <div className="cd-h" style={{background:"linear-gradient(135deg,#1e1b4b,#dc2626)"}}>
            <div className="cd-t" style={{color:"#fff"}}>🔴 Lost Asset Reports</div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <div className="cd-s" style={{color:"rgba(255,255,255,.75)"}}>{lostReports.length} reports</div>
              <button onClick={()=>exportLostCSV(lostFiltered)}
                style={{padding:"5px 12px",background:"rgba(255,255,255,.2)",border:"1px solid rgba(255,255,255,.3)",
                  borderRadius:7,color:"#fff",fontWeight:700,fontSize:11,cursor:"pointer"}}>
                ⬇ Export CSV
              </button>
            </div>
          </div>
          <div className="cd-b" style={{padding:"8px 0 0"}}>
            {/* Sub-filter */}
            <div style={{display:"flex",gap:6,padding:"0 14px 10px"}}>
              {["all","pending","Notified Employee"].map(f=>(
                <button key={f} onClick={()=>setLostFilter(f)}
                  style={{padding:"4px 12px",borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontWeight:600,
                    background:lostFilter===f?"#dc2626":"#f1f5f9",color:lostFilter===f?"#fff":"#64748b"}}>
                  {f==="all"?"All":f==="pending"?"Action Needed":"Notified"} ({f==="all"?lostReports.length:f==="pending"?lostReports.filter(r=>r.status!=="Notified Employee").length:lostReports.filter(r=>r.status==="Notified Employee").length})
                </button>
              ))}
            </div>
            <div style={{overflowX:"auto"}}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Report ID</th><th>Asset</th><th>Employee</th><th>PM</th>
                    <th>Asset Value</th><th>Fine (₹)</th><th>Lost Date</th>
                    <th>Status</th><th>PM Comment</th><th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {lostFiltered.length===0
                    ? <tr><td colSpan="10" style={{textAlign:"center",padding:20,color:"#94a3b8"}}>No lost asset reports</td></tr>
                    : lostFiltered.map(r=>(
                    <tr key={r.report_id} style={{background:r.hr_sent?"#f0fdf4":"#fff8f8"}}>
                      <td style={{fontWeight:600,color:"#dc2626",fontSize:12}}>{r.report_id}</td>
                      <td>
                        <div style={{fontWeight:600,fontSize:12}}>{r.asset_name||r.asset_id}</div>
                        <div style={{fontSize:10,color:"#94a3b8"}}>{r.asset_id} · {r.asset_tag||"—"}</div>
                      </td>
                      <td style={{fontSize:12}}>{r.employee_name||r.employee_full_name||"—"}</td>
                      <td style={{fontSize:12}}>{r.pm_name||r.pm_full_name||"—"}</td>
                      <td style={{fontSize:12,fontWeight:600}}>₹{Number(r.asset_value||0).toLocaleString("en-IN")}</td>
                      <td>
                        <span style={{fontWeight:700,color:r.fine_amount>0?"#dc2626":"#94a3b8",fontSize:13}}>
                          {r.fine_amount>0?`₹${Number(r.fine_amount).toLocaleString("en-IN")}`:"Nil"}
                        </span>
                      </td>
                      <td style={{fontSize:11,color:"#64748b"}}>{fmtDate(r.lost_date)}</td>
                      <td>
                        <span style={{padding:"3px 8px",borderRadius:20,fontSize:10,fontWeight:700,
                          background:r.status==="Notified Employee"?"#dcfce7":(r.status==="Sent to HR"||r.status==="PM Reviewed")?"#eff6ff":"#fef2f2",
                          color:r.status==="Notified Employee"?"#16a34a":(r.status==="Sent to HR"||r.status==="PM Reviewed")?"#2563eb":"#dc2626"}}>
                          {r.status||"Pending PM"}
                        </span>
                      </td>
                      <td style={{fontSize:11,color:"#64748b",maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {r.pm_comment||"—"}
                      </td>
                      <td>
                        {r.status==="Notified Employee"
                          ? <span style={{fontSize:11,color:"#16a34a",fontWeight:600}}>✅ Sent</span>
                          : (r.pm_comment || r.status==="Sent to HR" || r.status==="PM Reviewed")
                            ? <button onClick={()=>{setLostDetailModal(r);setHrNoticeComment("");}}
                                style={{padding:"5px 10px",background:"#dc2626",color:"#fff",border:"none",
                                  borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer"}}>
                                ⚠️ Send Notice
                              </button>
                            : <span style={{fontSize:11,color:"#94a3b8"}}>Awaiting PM</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="cd hrreportspg-inline-1">
        <div className="cd-h">
          <div className="cd-t">Send Report To Manager</div>
          <div className="cd-s">Upload any file type + comment</div>
        </div>
        <div className="cd-b hrreportspg-inline-2">
          <div className="hrreportspg-inline-3">
            <input className="fi" placeholder="Report title" value={form.title} onChange={(e) => setForm((v) => ({ ...v, title: e.target.value }))} />
            <select className="fi" value={form.reportType} onChange={(e) => setForm((v) => ({ ...v, reportType: e.target.value }))}>
              <option value="Compliance">Compliance</option>
              <option value="Attendance">Attendance</option>
              <option value="Performance">Performance</option>
            </select>
          </div>

          {/* ── Multi PM Picker ── */}
          <div style={{marginBottom:10}}>
            <label style={{fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:6}}>
              Project Managers *
            </label>
            <div style={{display:"flex",gap:8,alignItems:"flex-start",flexWrap:"wrap"}}>
              {/* Manager icon button */}
              <button type="button" onClick={()=>setShowPMPicker(true)}
                style={{display:"flex",alignItems:"center",gap:7,padding:"8px 14px",
                  border:"2px dashed #6366f1",borderRadius:9,background:"#f5f3ff",
                  color:"#4f46e5",fontWeight:700,fontSize:12,cursor:"pointer",
                  transition:"all 0.15s"}}>
                  {selectedPMs.length === 0 ? "Select Managers" : `+ Add More`}
              </button>
              {/* Selected PM chips */}
              {selectedPMs.map(pm => (
                <div key={pm.id} style={{display:"flex",alignItems:"center",gap:5,
                  background:"#ede9fe",border:"1px solid #c4b5fd",borderRadius:20,
                  padding:"5px 12px 5px 10px",fontSize:12,fontWeight:600,color:"#4f46e5"}}>
                  <span style={{fontSize:14}}>👤</span>
                  {pm.name}
                  <button onClick={()=>togglePM(pm)}
                    style={{background:"none",border:"none",cursor:"pointer",color:"#7c3aed",
                      fontWeight:800,fontSize:14,lineHeight:1,padding:"0 0 0 4px"}}>×</button>
                </div>
              ))}
              {selectedPMs.length === 0 && (
                <span style={{fontSize:11,color:"#94a3b8",alignSelf:"center"}}>No managers selected</span>
              )}
            </div>
          </div>
          <select className="fi" value={form.projectId} onChange={(e) => setForm((v) => ({ ...v, projectId: e.target.value }))}>
            <option value="">Select Project ID (optional)</option>
            {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.project_id} - {p.name}</option>)}
          </select>
          <textarea className="fta" rows={3} placeholder="Comment" value={form.comment} onChange={(e) => setForm((v) => ({ ...v, comment: e.target.value }))} />
          <input type="file" onChange={(e) => setForm((v) => ({ ...v, file: e.target.files?.[0] || null }))} />
          <div className="hrreportspg-inline-4">
            <button className="btn" onClick={sendReport} disabled={submitting || !selectedPMs.length || !form.file}>
              {II("send", 14)} {submitting ? "Sending…" : selectedPMs.length > 1 ? `Send to ${selectedPMs.length} Managers` : "Send To Manager"}
            </button>
          </div>
        </div>
      </div>

      <div className="cd">
        <div className="cd-h" style={{flexWrap:"wrap",gap:8}}>
          <div className="cd-t">
            Report Log
            {rptMonthFilter!=="all" && <span style={{fontSize:12,color:"#64748b",fontWeight:400,marginLeft:8}}>· {_mLabel(rptMonthFilter)}</span>}
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginLeft:"auto"}}>
            {/* Month filter */}
            <select value={rptMonthFilter} onChange={e=>setRptMonthFilter(e.target.value)}
              style={{padding:"6px 10px",border:"1px solid #e2e8f0",borderRadius:8,
                fontSize:12,color:"#374151",background:"#fff",cursor:"pointer"}}>
              <option value="all">All Months</option>
              {rptAvailMonths.filter(m=>m!=="all").map(m=>(
                <option key={m} value={m}>{_mLabel(m)}</option>
              ))}
            </select>
            {rptMonthFilter!=="all" && (
              <button onClick={()=>setRptMonthFilter("all")}
                style={{padding:"5px 9px",border:"1px solid #fecaca",borderRadius:8,
                  background:"#fef2f2",color:"#ef4444",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                ✕ Reset
              </button>
            )}
            {/* Monthly dropdown */}
            <div style={{position:"relative"}}>
              <button onClick={()=>setShowRptMDrop(v=>!v)}
                style={{display:"flex",alignItems:"center",gap:5,padding:"6px 11px",
                  background:"#fff",border:"1px solid #e2e8f0",borderRadius:8,
                  fontWeight:600,fontSize:12,color:"#374151",cursor:"pointer"}}>
                📅 Monthly CSV ▾
              </button>
              {showRptMDrop && (
                <div style={{position:"absolute",right:0,top:"calc(100% + 6px)",background:"#fff",
                  border:"1px solid #e2e8f0",borderRadius:10,boxShadow:"0 8px 24px rgba(0,0,0,.14)",
                  zIndex:300,minWidth:230,overflow:"hidden"}}>
                  <div style={{padding:"8px 12px",fontSize:11,fontWeight:700,color:"#64748b",
                    borderBottom:"1px solid #f1f5f9",background:"#f8fafc"}}>MONTH-WISE DOWNLOAD</div>
                  {rptAvailMonths.filter(m=>m!=="all").length===0 ? (
                    <div style={{padding:"12px",fontSize:12,color:"#94a3b8"}}>No data yet</div>
                  ) : rptAvailMonths.filter(m=>m!=="all").map(m=>{
                    const mRows=mine.filter(r=>_mKey(r.created_at)===m);
                    return (
                      <button key={m}
                        onClick={()=>{ exportReportsCSV(mRows,`reports_${m}.csv`); setShowRptMDrop(false); }}
                        style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                          width:"100%",padding:"10px 14px",border:"none",background:"#fff",
                          cursor:"pointer",fontSize:13,borderBottom:"1px solid #f8fafc",textAlign:"left"}}
                        onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                        onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                        <span style={{fontWeight:600,color:"#1e293b"}}>{_mLabel(m)}</span>
                        <span style={{fontSize:11,color:"#94a3b8"}}>{mRows.length} reports</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {/* Download current view */}
            <button onClick={()=>exportReportsCSV(displayReports, `reports${rptMonthFilter!=="all"?"_"+rptMonthFilter:""}.csv`)}
              style={{display:"flex",alignItems:"center",gap:6,padding:"6px 13px",
                background:"#0f172a",color:"#fff",border:"none",borderRadius:8,
                fontWeight:700,fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>
              ⬇ Download CSV {displayReports.length!==mine.length?`(${displayReports.length})`:""}
            </button>
            <div className="cd-s">{displayReports.length} reports</div>
          </div>
        </div>

        {/* Monthly banner */}
        {rptMonthFilter!=="all" && (
          <div style={{background:"linear-gradient(135deg,#1e1b4b,#4f46e5)",borderRadius:10,
            margin:"0 0 0 0",padding:"12px 18px",display:"flex",justifyContent:"space-between",
            alignItems:"center",color:"#fff"}}>
            <div>
              <div style={{fontWeight:700,fontSize:14}}>📅 {_mLabel(rptMonthFilter)}</div>
              <div style={{fontSize:11,opacity:.8,marginTop:2}}>{displayReports.length} reports sent this month</div>
            </div>
            <div style={{display:"flex",gap:14}}>
              {["Approved","Rejected","Pending"].map(s=>{
                const ct=displayReports.filter(r=>(r.status||"").toLowerCase()===s.toLowerCase()).length;
                return ct>0?(
                  <div key={s} style={{textAlign:"center"}}>
                    <div style={{fontSize:18,fontWeight:800}}>{ct}</div>
                    <div style={{fontSize:9,opacity:.75}}>{s}</div>
                  </div>
                ):null;
              })}
            </div>
            <button onClick={()=>exportReportsCSV(displayReports,`reports_${rptMonthFilter}.csv`)}
              style={{padding:"7px 14px",background:"rgba(255,255,255,.2)",border:"1px solid rgba(255,255,255,.3)",
                borderRadius:8,color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>
              ⬇ Download Month CSV
            </button>
          </div>
        )}

        <div className="cd-b hrreportspg-inline-5">
          {loading ? <div className="hrreportspg-inline-6">Loading...</div> :
          <table className="tbl">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Manager</th>
                  <th>Status</th>
                  <th>PM Comment</th>
                  {/* <th>File / Report</th>
                  <th>Action</th> */}
                </tr>
              </thead>
              <tbody>
                {displayReports.length === 0 ?
              <tr><td colSpan="7" className="hrreportspg-inline-7">{mine.length===0?"No reports":"No reports match the current filter"}</td></tr> :
              displayReports.map((r) =>
              <tr key={r.report_id}>
                    <td className="hrreportspg-inline-8">{r.report_id}</td>
                    <td className="hrreportspg-inline-9">{r.title}</td>
                    <td>{r.emp_name || r.pm_name || "-"}</td>
                    <td><span className={"chip " + (r.status === "Rejected" ? "chip-dn" : r.status === "Approved" ? "chip-ok" : "chip-wn")}>{r.status}</span></td>
                    <td className="hrreportspg-inline-10">{r.pm_note || "-"}</td>
                    <td>
                        {r.file_url ? (
                          <div className="hrreportspg-inline-11">
                            <button className="btn-o btn-s" onClick={() => handlePreview(r.file_url, r.file_name)}>
                        {II("eye", 12)} Preview
                      </button>
                            <button 
                              className="btn-o btn-s" 
                              onClick={() => api.downloadFile(r.file_url, r.file_name)}
                            >
                              Download
                            </button>
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                    {/* <td>
                      <button className="btn-o btn-s" onClick={() => openLogs(r)}>{II("note", 12)} View</button>
                    </td> */}
                  </tr>
              )}
              </tbody>
            </table>
          }
        </div>
      </div>

      {active &&
      <div className="modal">
          <div className="modal-card hrreportspg-inline-12">
            <div className="modal-head">
              <div className="modal-title">{active.title} ({active.report_id})</div>
              <button className="modal-close" onClick={() => setActive(null)}>✕</button>
            </div>
            <div className="modal-body hrreportspg-inline-13">
              <div className="hrreportspg-inline-14">
                <b>Status:</b> {active.status} | <b>PM Note:</b> {active.pm_note || "-"}
              </div>
              {active.status === "Rejected" &&
            <div className="hrreportspg-inline-15">
                  <div className="hrreportspg-inline-16">Resubmit Rejected Report</div>
                  <textarea className="fta" rows={3} placeholder="Your resubmission comment" value={resubmitComment} onChange={(e) => setResubmitComment(e.target.value)} />
                  <input type="file" onChange={(e) => setResubmitFile(e.target.files?.[0] || null)} className="hrreportspg-inline-17" />
                  <div className="hrreportspg-inline-18">
                    <button className="btn" onClick={resubmit} disabled={submitting || !resubmitFile}>{II("send", 14)} Re-Submit</button>
                  </div>
                </div>
            }
              <div className="hrreportspg-inline-19">Timeline</div>
              {logs.length === 0 ? <div className="hrreportspg-inline-20">No log entries</div> : logs.map((l) =>
            <div key={l.id} className="hrreportspg-inline-21">
                  <div className="hrreportspg-inline-22">{l.action}</div>
                  <div className="hrreportspg-inline-23">{l.note || "-"}</div>
                  <div className="hrreportspg-inline-24">{new Date(l.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                </div>
            )}
            </div>
          </div>
        </div>
      }

      {/* Lost Asset — HR Send to Employee Modal */}
      {lostDetailModal && (
        <div className="modal">
          <div className="modal-card" style={{maxWidth:560}}>
            <div className="modal-head" style={{background:"linear-gradient(135deg,#1e1b4b,#dc2626)"}}>
              <div className="modal-title" style={{color:"#fff"}}>🔴 Send Lost Asset Notice to Employee</div>
              <button className="modal-close" style={{color:"#fff"}} onClick={()=>setLostDetailModal(null)}>✕</button>
            </div>
            <div style={{padding:"16px 20px",overflowY:"auto",flex:1}}>
              {/* Report summary */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
                {[
                  ["Report ID",   lostDetailModal.report_id],
                  ["Asset",       `${lostDetailModal.asset_name||lostDetailModal.asset_id} (${lostDetailModal.asset_id})`],
                  ["Asset Tag",   lostDetailModal.asset_tag||"—"],
                  ["Asset Value", `₹${Number(lostDetailModal.asset_value||0).toLocaleString("en-IN")}`],
                  ["Fine Amount", lostDetailModal.fine_amount>0?`₹${Number(lostDetailModal.fine_amount).toLocaleString("en-IN")}`:"Nil"],
                  ["Lost Date",   fmtDate(lostDetailModal.lost_date)],
                  ["Issued Date", fmtDate(lostDetailModal.issued_date)],
                  ["Employee",    lostDetailModal.employee_name||lostDetailModal.employee_full_name||"—"],
                ].map(([l,v])=>(
                  <div key={l} style={{background:"#f8fafc",borderRadius:7,padding:"8px 10px"}}>
                    <div style={{fontSize:10,color:"#94a3b8",fontWeight:600,marginBottom:2}}>{l}</div>
                    <div style={{fontSize:13,fontWeight:700,color:"#0f172a"}}>{v}</div>
                  </div>
                ))}
              </div>
              {/* PM comment */}
              {lostDetailModal.pm_comment && (
                <div style={{background:"#eff6ff",borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:12,color:"#1e40af"}}>
                  <b>PM Comment:</b> {lostDetailModal.pm_comment}
                </div>
              )}
              {/* HR comment */}
              <div className="fg">
                <label className="fl">HR Message to Employee *</label>
                <textarea className="fi" rows={3}
                  value={hrNoticeComment}
                  onChange={e=>setHrNoticeComment(e.target.value)}
                  placeholder="e.g. Please acknowledge this notice regarding the lost asset and clear the fine..." />
              </div>
              <p style={{fontSize:11,color:"#64748b",lineHeight:1.6,margin:"8px 0 14px"}}>
                📬 This will send a notification to the employee with asset details, fine amount, and your message. Employee will see this in their Assets tab.
              </p>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                <button className="btn btn-o" onClick={()=>setLostDetailModal(null)}>Cancel</button>
                <button onClick={()=>handleSendToEmployee(lostDetailModal)}
                  disabled={sendingNotice===lostDetailModal.report_id}
                  style={{padding:"9px 20px",background:"#dc2626",color:"#fff",border:"none",
                    borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer"}}>
                  {sendingNotice===lostDetailModal.report_id?"Sending…":"⚠️ Send Notice to Employee"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── PM Picker Modal ── */}
      {showPMPicker && (
        <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,.5)",backdropFilter:"blur(4px)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:16,boxShadow:"0 24px 60px rgba(0,0,0,.2)",width:"90%",maxWidth:480,maxHeight:"85vh",overflow:"hidden",display:"flex",flexDirection:"column"}}>
            <div style={{background:"linear-gradient(135deg,#1e1b4b,#4f46e5)",padding:"16px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",borderRadius:"16px 16px 0 0"}}>
              <div style={{color:"#fff",fontWeight:700,fontSize:15}}>👤 Select Project Managers</div>
              <button style={{background:"rgba(255,255,255,.2)",border:"none",borderRadius:8,width:30,height:30,color:"#fff",cursor:"pointer",fontSize:16}} onClick={()=>setShowPMPicker(false)}>✕</button>
            </div>
            <div style={{padding:"16px 20px",overflowY:"auto",flex:1}}>
              {/* Search */}
              <input
                placeholder="🔍 Search by name or ID…"
                value={pmSearch}
                onChange={e=>setPmSearch(e.target.value)}
                style={{width:"100%",padding:"8px 12px",border:"1px solid #e2e8f0",
                  borderRadius:8,fontSize:12,marginBottom:12,boxSizing:"border-box",outline:"none"}}
              />
              {/* Selected count */}
              {selectedPMs.length > 0 && (
                <div style={{background:"#ede9fe",borderRadius:8,padding:"8px 12px",marginBottom:10,
                  fontSize:12,color:"#4f46e5",fontWeight:600,display:"flex",justifyContent:"space-between"}}>
                  <span>✅ {selectedPMs.length} manager{selectedPMs.length>1?"s":""} selected: {selectedPMs.map(p=>p.name).join(", ")}</span>
                  <button onClick={()=>setSelectedPMs([])}
                    style={{background:"none",border:"none",color:"#dc2626",cursor:"pointer",fontWeight:700,fontSize:12}}>
                    Clear All
                  </button>
                </div>
              )}
              {/* PM list */}
              <div style={{maxHeight:320,overflowY:"auto",display:"flex",flexDirection:"column",gap:6}}>
                {filteredPMs.length === 0
                  ? <div style={{textAlign:"center",padding:20,color:"#94a3b8"}}>No managers found</div>
                  : filteredPMs.map(pm => {
                    const isSelected = selectedPMs.find(p => p.id === pm.id);
                    return (
                      <div key={pm.id} onClick={()=>togglePM(pm)}
                        style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",
                          borderRadius:9,cursor:"pointer",transition:"all 0.15s",
                          background: isSelected ? "#ede9fe" : "#f8fafc",
                          border: `2px solid ${isSelected ? "#6366f1" : "#f1f5f9"}`,
                        }}>
                        {/* Avatar */}
                        <div style={{width:36,height:36,borderRadius:"50%",flexShrink:0,
                          background:isSelected?"#4f46e5":"#e2e8f0",
                          display:"flex",alignItems:"center",justifyContent:"center",
                          fontSize:16,color:isSelected?"#fff":"#64748b"}}>
                          👤
                        </div>
                        <div style={{flex:1}}>
                          <div style={{fontWeight:700,fontSize:13,color: isSelected?"#3730a3":"#0f172a"}}>{pm.name}</div>
                          <div style={{fontSize:11,color:"#94a3b8"}}>{pm.emp_id||"—"} · PM</div>
                        </div>
                        <div style={{width:20,height:20,borderRadius:"50%",
                          border:`2px solid ${isSelected?"#6366f1":"#d1d5db"}`,
                          background: isSelected?"#6366f1":"transparent",
                          display:"flex",alignItems:"center",justifyContent:"center",
                          flexShrink:0}}>
                          {isSelected && <span style={{color:"#fff",fontSize:11,fontWeight:800}}>✓</span>}
                        </div>
                      </div>
                    );
                  })
                }
              </div>
              <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:14}}>
                <button className="btn btn-o" onClick={()=>setShowPMPicker(false)}>Cancel</button>
                <button className="btn" onClick={()=>setShowPMPicker(false)}
                  disabled={!selectedPMs.length}
                  style={{opacity:selectedPMs.length?1:0.5}}>
                  ✅ Confirm ({selectedPMs.length} selected)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      

      {viewer.open &&
      <div className="hrreportspg-inline-25">
          <div className="hrreportspg-inline-26">
            <div className="hrreportspg-inline-27">
              <div className="hrreportspg-inline-28">{viewer.name}</div>
              <button className="btn-o btn-s" onClick={() => setViewer({ open: false, url: "", name: "" })}>Close</button>
            </div>
            <iframe title="hr-report-viewer" src={viewer.url} className="hrreportspg-inline-29" />
          </div>
        </div>
      }
      {previewData.open && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
          zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }}>
          <div style={{
            width: '100%', maxWidth: '1000px', height: '90vh', background: '#fff', 
            borderRadius: '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden'
          }}>
            <div style={{ padding: '15px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{fontSize: '16px'}}>{previewData.name}</strong>
              <button 
                onClick={() => {
                  window.URL.revokeObjectURL(previewData.url); // மெமரியை விடுவிக்க
                  setPreviewData({ open: false, url: "", name: "", mimeType: "" });
                }}
                style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '6px 15px', borderRadius: '6px', cursor: 'pointer' }}
              >
                Close Preview
              </button>
            </div>
            <div style={{ flex: 1, background: '#f9f9f9', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {previewData.mimeType.startsWith('image/') ? (
                <img src={previewData.url} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              ) : (
                <iframe src={previewData.url} title="File Preview" style={{ width: '100%', height: '100%', border: 'none' }} />
              )}
            </div>
          </div>
        </div>
      )}
  
    </div>);
    

}

export default HrReportsPg;
